import React, { useState } from "react";
import { COLORS, TYPOGRAPHY, SPACING, SHADOWS, RADIUS, TRANSITIONS } from '../constants/designTokens';
import { SITE } from '../constants/siteConfig';

const Contact = () => {
  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState({ isSubmitting: true, isSuccess: false, isError: false, errorMessage: '' });

    const formData = new FormData(e.target);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setFormState({ isSubmitting: false, isSuccess: true, isError: false, errorMessage: '' });
        e.target.reset();

        // Reset success message after 5 seconds
        setTimeout(() => {
          setFormState({ isSubmitting: false, isSuccess: false, isError: false, errorMessage: '' });
        }, 5000);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: error.message || 'Something went wrong. Please try again.'
      });
    }
  };

  return (
    <section id="contact" className="contact-section py-5" style={{
      background: COLORS.background.lightBlue,
      position: "relative"
    }}>
      <style>{`
        .contact-section h2 {
          color: ${COLORS.primary.dark};
          font-weight: ${TYPOGRAPHY.fontWeight.bold};
          font-size: ${TYPOGRAPHY.fontSize['2xl']};
          margin-bottom: ${SPACING['2xl']};
          text-align: center;
          line-height: ${TYPOGRAPHY.lineHeight.tight};
        }
        .contact-form {
          background: ${COLORS.background.white};
          border-radius: ${RADIUS.xl};
          box-shadow: ${SHADOWS.md};
          padding: ${SPACING['2xl']} ${SPACING.xl};
          max-width: 500px;
          margin: 0 auto 0 0;
          border: 1px solid ${COLORS.ui.border};
        }
        .form-label {
          color: ${COLORS.primary.dark};
          font-weight: ${TYPOGRAPHY.fontWeight.medium};
          margin-bottom: ${SPACING.xs};
          font-size: ${TYPOGRAPHY.fontSize.sm};
        }
        .form-control {
          border-radius: ${RADIUS.md};
          border: 1px solid ${COLORS.ui.border};
          padding: ${SPACING.sm} ${SPACING.md};
          transition: ${TRANSITIONS.colors};
          font-size: ${TYPOGRAPHY.fontSize.base};
        }
        .form-control:hover {
          border-color: ${COLORS.primary.teal};
        }
        .form-control:focus {
          border-color: ${COLORS.primary.teal};
          box-shadow: 0 0 0 3px rgba(64, 168, 196, 0.15);
          outline: none;
        }
        .btn-contact {
          background: ${COLORS.primary.teal};
          border: none;
          color: ${COLORS.text.white};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          border-radius: ${RADIUS.lg};
          padding: ${SPACING.md} ${SPACING.xl};
          margin-top: ${SPACING.sm};
          transition: ${TRANSITIONS.all};
          box-shadow: ${SHADOWS.md};
          font-size: ${TYPOGRAPHY.fontSize.base};
          position: relative;
        }
        .btn-contact:hover:not(:disabled) {
          background: ${COLORS.primary.dark};
          color: ${COLORS.text.white};
          transform: translateY(-2px);
          box-shadow: ${SHADOWS.hover.md};
        }
        .btn-contact:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(64, 168, 196, 0.3), ${SHADOWS.md};
        }
        .btn-contact:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .contact-success {
          color: ${COLORS.ui.success};
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid ${COLORS.ui.success};
          border-radius: ${RADIUS.md};
          padding: ${SPACING.md};
          text-align: center;
          font-size: ${TYPOGRAPHY.fontSize.base};
          margin-top: ${SPACING.md};
          font-weight: ${TYPOGRAPHY.fontWeight.medium};
        }
        .contact-error {
          color: ${COLORS.ui.error};
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid ${COLORS.ui.error};
          border-radius: ${RADIUS.md};
          padding: ${SPACING.md};
          text-align: center;
          font-size: ${TYPOGRAPHY.fontSize.base};
          margin-top: ${SPACING.md};
          font-weight: ${TYPOGRAPHY.fontWeight.medium};
        }
        .contact-info {
          color: ${COLORS.primary.dark};
          font-size: ${TYPOGRAPHY.fontSize.md};
          background: ${COLORS.background.lightTeal};
          border-radius: ${RADIUS.xl};
          box-shadow: ${SHADOWS.sm};
          padding: ${SPACING['2xl']} ${SPACING.xl};
          margin-left: 0;
          border: 1px solid ${COLORS.ui.border};
        }
        .contact-info h4 {
          color: ${COLORS.primary.teal};
          font-weight: ${TYPOGRAPHY.fontWeight.semibold};
          font-size: ${TYPOGRAPHY.fontSize.xl};
          margin-bottom: ${SPACING.lg};
        }
        .contact-info p {
          color: ${COLORS.text.primary};
          margin-bottom: ${SPACING.sm};
          line-height: ${TYPOGRAPHY.lineHeight.relaxed};
        }
        .contact-info a {
          color: ${COLORS.primary.teal};
          text-decoration: underline;
          transition: ${TRANSITIONS.colors};
        }
        .contact-info a:hover {
          color: ${COLORS.primary.dark};
        }
        .contact-info a:focus {
          outline: 2px solid ${COLORS.primary.teal};
          outline-offset: 2px;
        }
        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: ${COLORS.text.white};
          animation: spin 0.6s linear infinite;
          margin-left: ${SPACING.xs};
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 991.98px) {
          .contact-form, .contact-info {
            margin: 0 auto ${SPACING.xl} auto;
            max-width: 100%;
          }
        }
        @media (max-width: 767.98px) {
          .contact-section h2 {
            font-size: ${TYPOGRAPHY.fontSize.xl};
          }
          .contact-form, .contact-info {
            padding: ${SPACING.lg} ${SPACING.md};
          }
        }
      `}</style>
      <div className="container">
        <h2>Contact Us</h2>
        <div className="row justify-content-center align-items-stretch">
          <div className="col-lg-6 mb-4 mb-lg-0 d-flex align-items-center">
            <form
              className="contact-form w-100"
              onSubmit={handleSubmit}
              autoComplete="off"
              aria-label="Contact form"
            >
              <input type="hidden" name="access_key" value={SITE.web3formsKey} />
              <input type="hidden" name="subject" value="New Contact Form Submission from Marine Sealink Website" />
              <input type="hidden" name="from_name" value="Marine Sealink B.V. Website" />

              <div className="mb-3">
                <label htmlFor="name" className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  placeholder="Your name"
                  required
                  disabled={formState.isSubmitting}
                  aria-required="true"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  placeholder="your.email@example.com"
                  required
                  disabled={formState.isSubmitting}
                  aria-required="true"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="phone" className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-control"
                  id="phone"
                  name="phone"
                  placeholder="+31 (0)6 12 34 56 78"
                  disabled={formState.isSubmitting}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="message" className="form-label">Message *</label>
                <textarea
                  className="form-control"
                  id="message"
                  name="message"
                  rows="5"
                  placeholder="Tell us about your project or inquiry..."
                  required
                  disabled={formState.isSubmitting}
                  aria-required="true"
                ></textarea>
              </div>

              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn btn-contact btn-lg"
                  disabled={formState.isSubmitting}
                  aria-busy={formState.isSubmitting}
                >
                  {formState.isSubmitting ? (
                    <>
                      Sending...
                      <span className="spinner" role="status" aria-label="Loading"></span>
                    </>
                  ) : 'Send Message'}
                </button>
              </div>

              {formState.isSuccess && (
                <div className="contact-success" role="alert" aria-live="polite">
                  <i className="fa-solid fa-circle-check"></i> Thank you! Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {formState.isError && (
                <div className="contact-error" role="alert" aria-live="assertive">
                  <i className="fa-solid fa-circle-exclamation"></i> {formState.errorMessage}
                </div>
              )}
            </form>
          </div>

          <div className="col-lg-6 d-flex align-items-center">
            <div className="contact-info w-100 text-center text-lg-start">
              <h4>Do you need a quote?</h4>
              <p>Send us your parts list or equipment details for electrical spares, instrumentation &amp; control, OWS &amp; ODME, or fire &amp; smoke detection. Our team will source the right certified spares and respond promptly.</p>
              <p><strong>Email:</strong> <a href={`mailto:${SITE.email}`}>{SITE.email}</a></p>
              <p><strong>Mobile:</strong> <a href={SITE.phoneHref}>{SITE.phoneDisplay}</a></p>
              <p><strong>Office:</strong> {SITE.location}</p>
              <p><strong>KVK:</strong> {SITE.kvk} &nbsp;|&nbsp; <strong>VAT:</strong> {SITE.vat}</p>
              <p>
                <strong>LinkedIn:</strong>{' '}
                <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer">
                  <i className="fa-brands fa-linkedin"></i> Marine Sealink
                </a>
              </p>
              <p style={{
                color: COLORS.primary.teal,
                fontStyle: 'italic',
                marginTop: SPACING.xl,
                fontSize: TYPOGRAPHY.fontSize.md
              }}>"The right part, sourced fast, anywhere your vessel sails."</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
